/**
 * BATCH 23 PART 17 — E-commerce Continued Templates 269–280
 * Services: Shopify, Stripe, Calendly, Airtable, Google Merchant Center, Jira, Slack, OpenAI, Google Sheets
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "Shopify: Fetch order details", not "API Call")
 * 7. Each workflow has 8 nodes with proper edges
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 17 — TEMPLATES 269–280 (E-commerce continued)
// =============================================================================

export const ELITE_BATCH23_PART17: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 269. RESERVATION CONFIRMATION — Pattern E (Post-Purchase)
  // ===================================================================

  "Reservation Confirmation": {
    nodes: [
      trigger("Webhook: Reservation created in booking system", "webhook_app", {
        source: "Calendly",
        event: "reservation.created",
        howItStarts:
          "A webhook fires when a new reservation is created in the booking or scheduling system, including restaurant, salon, or service appointments",
        setup:
          "Connect your Calendly or booking system to POST reservation events to this workflow URL. Include guest details, date, time, and party size in the payload.",
        inputFields: [],
      }),

      // 2. runCode: Fetch reservation details and format
      n(
        "2",
        "runCode",
        "Extract reservation details and compute calendar link",
        240,
        {
          codeDetails: {
            code: `const payload = input.webhook_payload;
const reservation = JSON.parse(payload);
output.guest_name = reservation.guest_name || reservation.invitee_name;
output.guest_email = reservation.guest_email || reservation.invitee_email;
output.reservation_date = reservation.event_date || reservation.start_time?.split('T')[0];
output.reservation_time = reservation.event_time || reservation.start_time?.split('T')[1]?.substring(0,5);
output.party_size = reservation.party_size || reservation.guests || 1;
output.location_name = reservation.location_name || reservation.location;
output.location_address = reservation.location_address || reservation.address;
output.confirmation_code = reservation.confirmation_id || reservation.uri?.split('/').pop();
output.calendar_link = 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=Reservation+at+' + encodeURIComponent(output.location_name) + '&dates=' + output.reservation_date.replace(/-/g,'') + '/' + output.reservation_date.replace(/-/g,'');`,
          },
        },
      ),

      // 3. Email: Confirmation with calendar link
      emailNode(
        "3",
        "Email: Send reservation confirmation with calendar invite",
        480,
        "{guest_email}",
        "Your reservation at {location_name} is confirmed - {reservation_date}",
        "<h2>Reservation Confirmed</h2><p>Hi {guest_name},</p><p>Your reservation is all set:</p><ul><li><strong>Date:</strong> {reservation_date}</li><li><strong>Time:</strong> {reservation_time}</li><li><strong>Party size:</strong> {party_size}</li><li><strong>Location:</strong> {location_name}, {location_address}</li><li><strong>Confirmation:</strong> #{confirmation_code}</li></ul><p><a href='{calendar_link}'>Add to Google Calendar</a></p><p>Need to change your reservation? Reply to this email or call us directly.</p>",
      ),

      // 4. Delay until 24 hours before reservation
      n("4", "delayAction", "Wait until 24 hours before reservation", 720, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      // 5. Email: Reminder with directions
      emailNode(
        "5",
        "Email: Send day-before reminder with directions and parking info",
        960,
        "{guest_email}",
        "Reminder: Your reservation at {location_name} is tomorrow at {reservation_time}",
        "<h2>See You Tomorrow!</h2><p>Hi {guest_name},</p><p>Just a friendly reminder about your reservation tomorrow:</p><ul><li><strong>Time:</strong> {reservation_time}</li><li><strong>Party of:</strong> {party_size}</li><li><strong>Address:</strong> {location_address}</li></ul><p><a href='https://maps.google.com/?q={location_address}'>Get Directions</a></p><p>Please arrive 10 minutes early. If you need to cancel or modify, please let us know as soon as possible.</p>",
      ),

      // 6. Condition: Large party (6+)?
      n("6", "condition", "Is this a large party (6 or more guests)?", 1200, {
        conditionData: [
          {
            variable: "party_size",
            value: "6",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Slack: Notify staff about large party
      slackNode(
        "7",
        "Slack: Alert front-of-house staff about large party reservation",
        1440,
        "Large party reservation reminder for tomorrow at {reservation_time}: {guest_name}, party of {party_size}. Confirmation #{confirmation_code}. Please prepare accordingly.",
      ),

      // 8. Google Sheets: Log reservation
      sheetsNode(
        "8",
        "Google Sheets: Log reservation details and notification status",
        1680,
        "Reservations",
        '[["{ reservation_date }","{ reservation_time }","{ guest_name }","{ guest_email }","{ party_size }","{ confirmation_code }","{ location_name }","confirmed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Large party -> notify staff
      e("6", "8", "output_2"), // Normal party -> skip to log
      e("7", "8"),
    ],
    summary:
      "Confirms reservations and sends timely reminders with directions. When a reservation is created, the system extracts guest details and sends an immediate confirmation email with a calendar invite link. Twenty-four hours before the reservation, a reminder with directions and parking info is sent. Large parties of 6+ trigger a Slack alert to front-of-house staff, and all reservations are logged to Google Sheets for tracking.",
  },

  // ===================================================================
  // 270. RESERVATION REMINDER — Pattern D (Catalog Alert)
  // ===================================================================

  "Daily Reservation Reminder Batch": {
    nodes: [
      trigger(
        "Schedule: Fetch tomorrow's reservations daily at 8 AM",
        "schedule",
        {
          schedule: "Every day at 8:00 AM",
          howItStarts:
            "Runs daily at 8 AM to compile and send reminders for all reservations scheduled for the following day",
          setup:
            "Configure the schedule trigger to run daily at 8:00 AM in your timezone. Ensure your booking API endpoint is accessible with an API key.",
          inputFields: [],
        },
      ),

      // 2. API: Fetch tomorrow's reservations
      n(
        "2",
        "hitApi",
        "Calendly: Fetch all scheduled events for tomorrow",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.calendly.com/scheduled_events?min_start_time={tomorrow_start}&max_start_time={tomorrow_end}&status=active",
            headers: [
              { key: "Authorization", value: "Bearer {calendly_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "collection",
                customName: "reservations_list",
                type: "array",
              },
              {
                name: "pagination.count",
                customName: "reservation_count",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. runCode: Compile reminders
      n(
        "3",
        "runCode",
        "Compile individual reminder messages from reservation data",
        480,
        {
          codeDetails: {
            code: `const reservations = input.reservations_list || [];
output.total_reservations = reservations.length;
output.reminder_batch = reservations.map(r => ({
  guest_name: r.name || r.invitee_name,
  guest_email: r.email || r.invitee_email,
  guest_phone: r.phone || r.invitee_phone || '',
  event_time: r.start_time?.split('T')[1]?.substring(0,5) || r.time,
  party_size: r.guests || 1,
  confirmation_code: r.uri?.split('/').pop() || r.confirmation_id,
}));
output.has_reservations = reservations.length > 0;`,
          },
        },
      ),

      // 4. Email: Send reminders to each guest
      emailNode(
        "4",
        "Email: Send reservation reminder to each guest for tomorrow",
        720,
        "{guest_email}",
        "Reminder: Your reservation is tomorrow at {event_time}",
        "<h2>Your Reservation is Tomorrow</h2><p>Hi {guest_name},</p><p>We're looking forward to seeing you tomorrow at <strong>{event_time}</strong>.</p><p>Confirmation: #{confirmation_code}</p><p>If you need to make changes, please contact us as soon as possible.</p>",
      ),

      // 5. SMS: Same-day alert
      smsNode(
        "5",
        "SMS: Send same-day reservation reminder to guests with phone numbers",
        960,
        "{guest_phone}",
        "Reminder: You have a reservation tomorrow at {event_time}. Confirmation #{confirmation_code}. Reply CANCEL to cancel.",
      ),

      // 6. Condition: Any guest has not confirmed?
      n(
        "6",
        "condition",
        "Are there any unconfirmed reservations (no reply received)?",
        1200,
        {
          conditionData: [
            {
              variable: "has_reservations",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Slack: Flag no-show risk
      slackNode(
        "7",
        "Slack: Flag unconfirmed reservations as potential no-show risk",
        1440,
        "No-show risk alert: {total_reservations} reservations for tomorrow. Some guests have not confirmed. Please follow up manually on unconfirmed bookings.",
      ),

      // 8. Google Sheets: Log reminder batch
      sheetsNode(
        "8",
        "Google Sheets: Log daily reminder batch results and counts",
        1680,
        "ReminderLog",
        '[["{ reservation_count }","reminders_sent","{ total_reservations }","batch_complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Unconfirmed reservations -> flag no-show risk
      e("6", "8", "output_2"), // All confirmed -> skip to log
      e("7", "8"),
    ],
    summary:
      "Sends daily batch reservation reminders for the next day. Every morning at 8 AM, the workflow fetches all active reservations for tomorrow from Calendly, compiles individual reminder messages, sends email reminders and SMS alerts, flags unconfirmed reservations as no-show risks via Slack, and logs the batch run to Google Sheets.",
  },

  // ===================================================================
  // 271. STORE APPOINTMENT REMINDER — Pattern D (Catalog Alert)
  // ===================================================================

  "Store Appointment Reminder": {
    nodes: [
      trigger(
        "Schedule: Fetch today's store appointments at 7 AM",
        "schedule",
        {
          schedule: "Every day at 7:00 AM",
          howItStarts:
            "Runs daily at 7 AM to pull all store appointments for the day and send reminders to customers and staff",
          setup:
            "Set the schedule to run at 7:00 AM daily. Connect your Calendly or appointment system with API credentials. Configure the store location details.",
          inputFields: [],
        },
      ),

      // 2. API: Fetch today's appointments
      n(
        "2",
        "hitApi",
        "Calendly: Fetch all appointments scheduled for today",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.calendly.com/scheduled_events?min_start_time={today_start}&max_start_time={today_end}&status=active",
            headers: [
              { key: "Authorization", value: "Bearer {calendly_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "collection",
                customName: "appointments_today",
                type: "array",
              },
              {
                name: "pagination.count",
                customName: "appointment_count",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. runCode: Format appointment details
      n(
        "3",
        "runCode",
        "Format appointment details with customer names, times, and services",
        480,
        {
          codeDetails: {
            code: `const appts = input.appointments_today || [];
output.total_appointments = appts.length;
output.formatted_list = appts.map(a => ({
  customer_name: a.name || a.invitee_name,
  customer_email: a.email || a.invitee_email,
  customer_phone: a.phone || '',
  appointment_time: a.start_time?.split('T')[1]?.substring(0,5) || a.time,
  service_type: a.event_type_name || a.service || 'General',
  staff_assigned: a.assigned_to || 'Unassigned',
  checked_in: false,
}));
output.has_appointments = appts.length > 0;`,
          },
        },
      ),

      // 4. Email: Send morning reminder
      emailNode(
        "4",
        "Email: Send morning appointment reminder to each customer",
        720,
        "{customer_email}",
        "Your appointment today at {appointment_time} - Don't forget!",
        "<h2>Your Appointment is Today</h2><p>Hi {customer_name},</p><p>This is a reminder about your <strong>{service_type}</strong> appointment today at <strong>{appointment_time}</strong>.</p><p>Please arrive 5-10 minutes early. If you need to reschedule, please call us as soon as possible.</p>",
      ),

      // 5. SMS: 1-hour before reminder
      smsNode(
        "5",
        "SMS: Send 1-hour-before reminder to customers with phone numbers",
        960,
        "{customer_phone}",
        "Reminder: Your {service_type} appointment is in 1 hour at {appointment_time}. See you soon! Reply HELP for directions.",
      ),

      // 6. Condition: Any customer did not check in?
      n(
        "6",
        "condition",
        "Has any customer not checked in within 15 minutes of appointment time?",
        1200,
        {
          conditionData: [
            {
              variable: "checked_in",
              value: "false",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Slack: Alert staff about no-show
      slackNode(
        "7",
        "Slack: Alert store staff about customer who has not checked in",
        1440,
        "No check-in alert: {customer_name} has not arrived for their {service_type} appointment at {appointment_time}. Staff assigned: {staff_assigned}. Please attempt a courtesy call.",
      ),

      // 8. Google Sheets: Log appointments and attendance
      sheetsNode(
        "8",
        "Google Sheets: Log today's appointments and check-in status",
        1680,
        "StoreAppointments",
        '[["{ customer_name }","{ customer_email }","{ appointment_time }","{ service_type }","{ staff_assigned }","{ checked_in }","{ total_appointments }"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Not checked in -> alert staff
      e("6", "8", "output_2"), // Checked in -> log directly
      e("7", "8"),
    ],
    summary:
      "Manages daily store appointment reminders and attendance tracking. Every morning at 7 AM, fetches all appointments for the day from Calendly, formats details including customer names and services, sends email reminders and SMS 1-hour alerts, flags no-shows to store staff via Slack, and logs appointment status to Google Sheets for operational reporting.",
  },

  // ===================================================================
  // 272. SERVICE APPOINTMENT FOLLOW-UP — Pattern E (Post-Purchase)
  // ===================================================================

  "Service Appointment Follow-Up": {
    nodes: [
      trigger(
        "Webhook: Service appointment marked as completed",
        "webhook_app",
        {
          source: "Calendly",
          event: "appointment.completed",
          howItStarts:
            "A webhook fires when a service appointment is marked as completed in the booking system, triggering a follow-up sequence with service summary and satisfaction survey",
          setup:
            "Configure Calendly or your booking system to POST a completed event when an appointment is finished. Include service details, staff info, and customer contact in the payload.",
          inputFields: [],
        },
      ),

      // 2. runCode: Fetch and format appointment details
      n(
        "2",
        "runCode",
        "Extract completed appointment details and compute follow-up timing",
        240,
        {
          codeDetails: {
            code: `const payload = input.webhook_payload;
const appt = JSON.parse(payload);
output.customer_name = appt.invitee_name || appt.customer_name;
output.customer_email = appt.invitee_email || appt.customer_email;
output.service_type = appt.event_type_name || appt.service_type;
output.staff_name = appt.assigned_to || appt.staff_name;
output.completion_time = appt.end_time || new Date().toISOString();
output.service_notes = appt.notes || 'No additional notes';
output.appointment_duration = appt.duration_minutes || 60;
output.next_recommended = appt.next_appointment || '';`,
          },
        },
      ),

      // 3. Email: Service summary
      emailNode(
        "3",
        "Email: Send service summary with care instructions and next steps",
        480,
        "{customer_email}",
        "Your {service_type} appointment summary - Thanks for visiting!",
        "<h2>Service Complete</h2><p>Hi {customer_name},</p><p>Thank you for your <strong>{service_type}</strong> appointment with {staff_name} today.</p><h3>Service Summary</h3><p>{service_notes}</p><p>If you have any questions or concerns about your service, don't hesitate to reach out.</p><p>We recommend scheduling your next appointment in 4-6 weeks to maintain results.</p>",
      ),

      // 4. Delay 4 hours
      n(
        "4",
        "delayAction",
        "Wait 4 hours after service for satisfaction survey",
        720,
        {
          delayDetails: { duration: 4, unit: "hours" },
        },
      ),

      // 5. Email: Satisfaction survey
      emailNode(
        "5",
        "Email: Send satisfaction survey with rating link",
        960,
        "{customer_email}",
        "How was your {service_type} today, {customer_name}?",
        "<h2>We'd Love Your Feedback</h2><p>Hi {customer_name},</p><p>How was your experience with {staff_name} today?</p><p>Please take 30 seconds to rate your visit:</p><p><a href='https://survey.example.com/rate?score=5&appt={completion_time}'>Excellent</a> | <a href='https://survey.example.com/rate?score=4&appt={completion_time}'>Good</a> | <a href='https://survey.example.com/rate?score=3&appt={completion_time}'>Average</a> | <a href='https://survey.example.com/rate?score=2&appt={completion_time}'>Below Average</a> | <a href='https://survey.example.com/rate?score=1&appt={completion_time}'>Poor</a></p><p>Your feedback helps us improve.</p>",
      ),

      // 6. Condition: Negative feedback received?
      n(
        "6",
        "condition",
        "Did customer submit a negative satisfaction rating (score 2 or below)?",
        1200,
        {
          conditionData: [
            {
              variable: "satisfaction_score",
              value: "2",
              comparison_operator: "LESS THAN OR EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Slack: Alert manager about negative feedback
      slackNode(
        "7",
        "Slack: Alert store manager about negative service feedback",
        1440,
        "Negative feedback alert: {customer_name} rated their {service_type} appointment with {staff_name} poorly. Please follow up with a recovery call within 24 hours.",
      ),

      // 8. Google Sheets: Log follow-up
      sheetsNode(
        "8",
        "Google Sheets: Log service completion and feedback status",
        1680,
        "ServiceFollowUp",
        '[["{ customer_name }","{ customer_email }","{ service_type }","{ staff_name }","{ completion_time }","{ appointment_duration }","feedback_pending"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Negative feedback -> alert manager
      e("6", "8", "output_2"), // Positive or no feedback -> log
      e("7", "8"),
    ],
    summary:
      "Automates post-service follow-up with summary and satisfaction survey. When an appointment is marked complete, the workflow sends an immediate service summary email with care instructions, waits 4 hours, then sends a satisfaction survey. Negative ratings trigger a Slack alert to the store manager for service recovery, and all interactions are logged to Google Sheets.",
  },

  // ===================================================================
  // 273. SHOPIFY REVIEW TO MARKETING ASSET — Pattern J (Advocacy)
  // ===================================================================

  "Shopify Review to Marketing Asset": {
    nodes: [
      trigger(
        "Webhook: 5-star product review submitted on Shopify",
        "webhook_app",
        {
          source: "Shopify",
          event: "product_review.created",
          howItStarts:
            "Shopify fires a webhook when a customer submits a 5-star product review, indicating a potential marketing testimonial asset",
          setup:
            "Configure Shopify review app (Judge.me, Yotpo, or Shopify Reviews) to POST review events to this workflow URL. Filter for 4-5 star reviews.",
          inputFields: [],
        },
      ),

      // 2. AI: Extract quotable text from review
      aiNode(
        "2",
        "AI: Extract quotable text and sentiment from 5-star review",
        240,
        "You are a marketing content specialist. Extract the reviewer_name, reviewer_email, review_text, product_name, product_id, star_rating, quotable_snippet (the most compelling 1-2 sentences), sentiment_theme (quality, value, service, design), and has_photo (boolean) from the review webhook. Return JSON.",
        "Extract marketing-worthy content from this product review: {webhook_payload}",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "reviewer_email", as: "reviewer_email", type: "string" },
          { field: "review_text", as: "review_text", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "quotable_snippet", as: "quotable_snippet", type: "string" },
          { field: "sentiment_theme", as: "sentiment_theme", type: "string" },
          { field: "has_photo", as: "has_photo", type: "boolean" },
        ],
      ),

      // 3. runCode: Check consent status
      n(
        "3",
        "runCode",
        "Check if reviewer has previously consented to marketing use of reviews",
        480,
        {
          codeDetails: {
            code: `output.reviewer_name = input.reviewer_name;
output.reviewer_email = input.reviewer_email;
output.consent_status = input.review_text.toLowerCase().includes('consent') ? 'granted' : 'pending';
output.quotable_snippet = input.quotable_snippet;
output.product_name = input.product_name;
output.sentiment_theme = input.sentiment_theme;
output.has_photo = input.has_photo;
output.asset_type = input.has_photo ? 'photo_testimonial' : 'text_testimonial';`,
          },
        },
      ),

      // 4. Condition: Does reviewer consent to marketing use?
      n(
        "4",
        "condition",
        "Has the reviewer consented to marketing use of their review?",
        720,
        {
          conditionData: [
            {
              variable: "consent_status",
              value: "granted",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. AI: Format review as social media asset
      aiNode(
        "5",
        "AI: Format 5-star review into social media marketing asset",
        960,
        "You are a social media content creator. Take this customer review and format it as a polished social media testimonial asset. Create social_copy (Instagram/Facebook post text with the quote, product name, and relevant hashtags), card_headline (short bold headline), and card_body (formatted quote with attribution). Return JSON.",
        "Create a social media testimonial from: Product: {product_name}, Quote: {quotable_snippet}, Reviewer: {reviewer_name}, Theme: {sentiment_theme}",
        [
          { field: "social_copy", as: "social_copy", type: "string" },
          { field: "card_headline", as: "card_headline", type: "string" },
          { field: "card_body", as: "card_body", type: "string" },
        ],
      ),

      // 6. Approval gate: Marketing team review
      n(
        "6",
        "approvalGate",
        "Marketing team: Approve testimonial asset for publishing",
        1200,
        {
          approvalGateDetails: {
            approverEmail: "marketing@company.com",
            message:
              'New 5-star testimonial asset for {product_name} from {reviewer_name}: "{quotable_snippet}". Social copy ready for review.',
          },
        },
      ),

      // 7. Airtable: Add to content library
      n(
        "7",
        "hitApi",
        "Airtable: Add approved testimonial to marketing asset library",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{base_id}/Testimonials",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"Reviewer":"{reviewer_name}","Product":"{product_name}","Quote":"{quotable_snippet}","Social Copy":"{social_copy}","Headline":"{card_headline}","Theme":"{sentiment_theme}","Asset Type":"{asset_type}","Status":"Approved"}',
              },
            ],
            variables: [
              { name: "id", customName: "airtable_record_id", type: "string" },
              {
                name: "createdTime",
                customName: "record_created",
                type: "string",
              },
            ],
          },
        },
      ),

      // 8. Slack: Notify team
      slackNode(
        "8",
        "Slack: Notify marketing team about new approved testimonial asset",
        1680,
        'New testimonial asset approved and added to library: "{quotable_snippet}" - {reviewer_name} on {product_name} ({sentiment_theme}). Social copy is ready to schedule. Airtable record: {airtable_record_id}',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Consent granted -> format as asset
      e("4", "8", "output_2"), // No consent -> just notify (skip asset creation)
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Transforms 5-star Shopify reviews into reusable marketing testimonial assets. When a top-rated review is submitted, AI extracts quotable text and sentiment themes, checks consent status, then AI formats the review into social media copy with headlines. After marketing team approval, the asset is added to an Airtable content library and the team is notified via Slack.",
  },

  // ===================================================================
  // 274. PRODUCT FEED ERROR ALERT — Pattern L (Usage/Expansion)
  // ===================================================================

  "Product Feed Error Alert": {
    nodes: [
      trigger(
        "Schedule: Check Google Merchant Center product feed daily",
        "schedule",
        {
          schedule: "Every day at 6:00 AM",
          howItStarts:
            "Runs daily at 6 AM to check the Google Merchant Center feed for disapproved products, data quality issues, and feed processing errors",
          setup:
            "Configure Google Merchant Center API access with a service account. Set the merchant_id and ensure the Content API for Shopping is enabled.",
          inputFields: [],
        },
      ),

      // 2. Google Merchant Center: Check feed status
      n(
        "2",
        "hitApi",
        "Google Merchant Center: Fetch product feed status and diagnostics",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/productstatuses?maxResults=250",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "resources",
                customName: "product_statuses",
                type: "array",
              },
              { name: "kind", customName: "response_kind", type: "string" },
            ],
          },
        },
      ),

      // 3. runCode: Identify errors and issues
      n(
        "3",
        "runCode",
        "Analyze product statuses and identify feed errors and disapprovals",
        480,
        {
          codeDetails: {
            code: `const statuses = input.product_statuses || [];
const errors = statuses.filter(p => p.destinationStatuses?.some(d => d.status === 'disapproved'));
const warnings = statuses.filter(p => p.itemLevelIssues?.length > 0);
output.error_count = errors.length;
output.warning_count = warnings.length;
output.total_products = statuses.length;
output.error_products = errors.slice(0, 20).map(p => p.title + ' (' + p.productId + ')').join(', ');
output.top_issues = [...new Set(errors.flatMap(p => p.itemLevelIssues?.map(i => i.description) || []))].slice(0, 5).join('; ');
output.has_errors = errors.length > 0;
output.error_rate = statuses.length > 0 ? (errors.length / statuses.length * 100).toFixed(1) : '0';`,
          },
        },
      ),

      // 4. Condition: Errors found?
      n(
        "4",
        "condition",
        "Are there any disapproved products or feed processing errors?",
        720,
        {
          conditionData: [
            {
              variable: "has_errors",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Slack: Alert merchandising team
      slackNode(
        "5",
        "Slack: Alert merchandising team about product feed errors",
        960,
        "Google Merchant Center Feed Alert: {error_count} products disapproved out of {total_products} ({error_rate}% error rate). Top issues: {top_issues}. Affected products: {error_products}. Please review and fix.",
      ),

      // 6. Email: Detailed error report
      emailNode(
        "6",
        "Email: Send detailed product feed error report to merchandising",
        1200,
        "{merchandising_email}",
        "Google Merchant Center: {error_count} Product Errors Detected",
        "<h2>Product Feed Error Report</h2><p><strong>Total Products:</strong> {total_products}</p><p><strong>Disapproved:</strong> {error_count} ({error_rate}%)</p><p><strong>Warnings:</strong> {warning_count}</p><h3>Top Issues</h3><p>{top_issues}</p><h3>Affected Products</h3><p>{error_products}</p><p>Please log into <a href='https://merchants.google.com'>Google Merchant Center</a> to review and resolve these issues.</p>",
      ),

      // 7. Google Sheets: Log errors
      sheetsNode(
        "7",
        "Google Sheets: Log daily feed health metrics and error counts",
        1440,
        "FeedHealth",
        '[["{ total_products }","{ error_count }","{ warning_count }","{ error_rate }","{ top_issues }"]]',
      ),

      // 8. runCode: Suggest fixes
      n(
        "8",
        "runCode",
        "Generate fix suggestions based on common Merchant Center error patterns",
        1680,
        {
          codeDetails: {
            code: `const issues = input.top_issues || '';
const suggestions = [];
if (issues.includes('GTIN')) suggestions.push('Add valid GTIN/UPC barcodes to product data');
if (issues.includes('image')) suggestions.push('Replace low-quality or missing product images');
if (issues.includes('price')) suggestions.push('Ensure prices match landing page and include currency');
if (issues.includes('shipping')) suggestions.push('Review shipping settings in Merchant Center');
if (issues.includes('description')) suggestions.push('Add detailed product descriptions over 100 characters');
if (suggestions.length === 0) suggestions.push('Review individual product diagnostics in Merchant Center');
output.fix_suggestions = suggestions.join('; ');
output.priority = input.error_count > 50 ? 'CRITICAL' : input.error_count > 10 ? 'HIGH' : 'MEDIUM';`,
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Errors found -> alert team
      e("4", "7", "output_2"), // No errors -> just log
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors Google Merchant Center product feed health daily. At 6 AM, the workflow checks all product statuses for disapprovals and data quality issues, computes error rates, alerts the merchandising team via Slack and email with detailed reports, logs daily feed health to Google Sheets, and generates automated fix suggestions based on common error patterns.",
  },

  // ===================================================================
  // 275. MARKETPLACE ORDER SYNC — Pattern B (Cart/Order)
  // ===================================================================

  "Marketplace Order Sync": {
    nodes: [
      trigger(
        "Webhook: New order received from marketplace (Amazon/eBay)",
        "webhook_app",
        {
          source: "Amazon",
          event: "order.created",
          howItStarts:
            "A marketplace (Amazon, eBay, Walmart) fires a webhook when a new order is placed, triggering synchronization to Shopify and inventory update across channels",
          setup:
            "Configure Amazon SP-API or eBay notifications to POST new order events to this workflow URL. Include order details, shipping address, and item SKUs.",
          inputFields: [],
        },
      ),

      // 2. AI: Normalize order format
      aiNode(
        "2",
        "AI: Normalize marketplace order data into standard format",
        240,
        "You are an e-commerce order processing specialist. Normalize the marketplace order into a standard format: order_id, marketplace (amazon/ebay/walmart), customer_name, customer_email, shipping_address, items (array of sku, title, quantity, price), order_total, currency, and shipping_method. Handle format variations between Amazon, eBay, and Walmart. Return JSON.",
        "Normalize this marketplace order into standard format: {webhook_payload}",
        [
          { field: "order_id", as: "marketplace_order_id", type: "string" },
          { field: "marketplace", as: "marketplace_source", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "items", as: "order_items", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "shipping_method", as: "shipping_method", type: "string" },
        ],
      ),

      // 3. Shopify: Create order
      n(
        "3",
        "hitApi",
        "Shopify: Create matching order in Shopify for fulfillment tracking",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "order",
                value:
                  '{"line_items":{order_items},"financial_status":"paid","source_name":"{marketplace_source}","note":"Marketplace order #{marketplace_order_id}","tags":"{marketplace_source},marketplace-sync"}',
              },
            ],
            variables: [
              {
                name: "order.id",
                customName: "shopify_order_id",
                type: "string",
              },
              {
                name: "order.order_number",
                customName: "shopify_order_number",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. runCode: Sync inventory across channels
      n(
        "4",
        "runCode",
        "Calculate updated inventory levels after marketplace sale",
        720,
        {
          codeDetails: {
            code: `const items = JSON.parse(input.order_items || '[]');
output.inventory_updates = items.map(item => ({
  sku: item.sku,
  quantity_sold: item.quantity,
  new_available: (item.current_stock || 0) - item.quantity,
}));
output.low_stock_items = output.inventory_updates.filter(i => i.new_available < 5);
output.has_low_stock = output.low_stock_items.length > 0;
output.total_items_sold = items.reduce((sum, i) => sum + i.quantity, 0);`,
          },
        },
      ),

      // 5. Condition: Any items low stock?
      n(
        "5",
        "condition",
        "Are any items below the low-stock threshold (fewer than 5 units)?",
        960,
        {
          conditionData: [
            {
              variable: "has_low_stock",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Slack: Alert about low stock
      slackNode(
        "6",
        "Slack: Alert operations about low-stock items after marketplace sale",
        1200,
        "Low stock alert after {marketplace_source} order #{marketplace_order_id}: Items running low after syncing {total_items_sold} units. Low stock SKUs: {low_stock_items}. Shopify order #{shopify_order_number} created.",
      ),

      // 7. Email: Notify supplier about low stock
      emailNode(
        "7",
        "Email: Notify supplier about reorder needs for low-stock items",
        1440,
        "{supplier_email}",
        "Reorder Alert: Low stock items after marketplace sales",
        "<h2>Reorder Required</h2><p>The following items are running low after recent marketplace sales:</p><p>{low_stock_items}</p><p>Please confirm lead times and availability for restocking.</p><p>Marketplace: {marketplace_source}<br/>Order: #{marketplace_order_id}<br/>Shopify: #{shopify_order_number}</p>",
      ),

      // 8. Google Sheets: Reconciliation log
      sheetsNode(
        "8",
        "Google Sheets: Log marketplace order sync for reconciliation",
        1680,
        "MarketplaceSync",
        '[["{ marketplace_order_id }","{ marketplace_source }","{ shopify_order_id }","{ order_total }","{ total_items_sold }","{ has_low_stock }","synced"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Low stock -> alert Slack
      e("5", "8", "output_2"), // Stock OK -> skip to log
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Synchronizes marketplace orders from Amazon/eBay into Shopify for unified fulfillment. When a marketplace order is received, AI normalizes the order format, creates a matching Shopify order, calculates inventory impact, and checks for low-stock items. Low stock triggers Slack alerts and supplier reorder emails, and all syncs are logged to Google Sheets for reconciliation.",
  },

  // ===================================================================
  // 276. COD CONFIRMATION WORKFLOW — Pattern F (Renewal/Dunning)
  // ===================================================================

  "COD Confirmation Workflow": {
    nodes: [
      trigger(
        "Webhook: Cash-on-delivery order placed on Shopify",
        "webhook_app",
        {
          source: "Shopify",
          event: "orders/create",
          howItStarts:
            "Shopify fires a webhook when a new cash-on-delivery order is placed, initiating a confirmation and preparation sequence for COD fulfillment",
          setup:
            "Configure Shopify webhook for orders/create. The workflow filters for COD payment method. Include customer contact details and order total in the payload.",
          inputFields: [],
        },
      ),

      // 2. Email: Order confirmation
      emailNode(
        "2",
        "Email: Send COD order confirmation with amount and delivery estimate",
        240,
        "{customer_email}",
        "Order Confirmed #{order_number} - Cash on Delivery",
        "<h2>Your COD Order is Confirmed</h2><p>Hi {customer_name},</p><p>Your order <strong>#{order_number}</strong> has been confirmed for cash on delivery.</p><p><strong>Amount due at delivery:</strong> {order_total} {currency}</p><p>Please have the exact amount ready when our delivery partner arrives. We'll send you tracking updates as your order ships.</p>",
      ),

      // 3. SMS: COD amount reminder
      smsNode(
        "3",
        "SMS: Send COD amount reminder with order details",
        480,
        "{customer_phone}",
        "Order #{order_number} confirmed (COD). Please keep {order_total} {currency} ready for delivery. We'll notify you before arrival.",
      ),

      // 4. Delay until delivery day minus 1
      n(
        "4",
        "delayAction",
        "Wait until 1 day before estimated delivery date",
        720,
        {
          delayDetails: { duration: 2, unit: "days" },
        },
      ),

      // 5. SMS: Prepare cash reminder
      smsNode(
        "5",
        "SMS: Remind customer to prepare exact cash amount for tomorrow",
        960,
        "{customer_phone}",
        "Delivery tomorrow for order #{order_number}! Please have {order_total} {currency} in cash ready. Track: {tracking_url}",
      ),

      // 6. Condition: Order delivered?
      n(
        "6",
        "condition",
        "Has the delivery partner confirmed successful delivery?",
        1200,
        {
          conditionData: [
            {
              variable: "delivery_status",
              value: "delivered",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Shopify: Mark as paid
      n(
        "7",
        "hitApi",
        "Shopify: Mark COD order as paid after successful delivery",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{shopify_order_id}/transactions.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "transaction",
                value:
                  '{"kind":"capture","amount":"{order_total}","gateway":"cash_on_delivery"}',
              },
            ],
            variables: [
              {
                name: "transaction.id",
                customName: "transaction_id",
                type: "string",
              },
              {
                name: "transaction.status",
                customName: "payment_status",
                type: "string",
              },
            ],
          },
        },
      ),

      // 8. Google Sheets: Log COD order lifecycle
      sheetsNode(
        "8",
        "Google Sheets: Log COD order lifecycle from confirmation to payment",
        1680,
        "CODOrders",
        '[["{ order_number }","{ customer_name }","{ order_total }","{ delivery_status }","{ payment_status }","{ transaction_id }","complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Delivered -> mark paid
      e("6", "8", "output_2"), // Not delivered -> log as pending
      e("7", "8"),
    ],
    summary:
      "Manages the full cash-on-delivery order lifecycle from confirmation to payment capture. When a COD order is placed on Shopify, the customer receives an email confirmation and SMS with the exact amount due. A day before delivery, a cash preparation reminder is sent. Once delivery is confirmed, the Shopify order is marked as paid, and the complete lifecycle is logged to Google Sheets.",
  },

  // ===================================================================
  // 277. FLASH SALE LAUNCH CHECKLIST — Pattern I (Content/Event)
  // ===================================================================

  "Flash Sale Launch Checklist": {
    nodes: [
      trigger(
        "Webhook: Flash sale approved and ready to launch in Airtable",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.updated",
          howItStarts:
            "Airtable fires a webhook when a flash sale record status changes to approved, triggering the automated launch sequence including inventory validation, price updates, and customer notifications",
          setup:
            "Configure Airtable automation to POST when a Flash Sales table record status changes to 'Approved'. Include sale name, discount percentage, start/end times, and product IDs.",
          inputFields: [],
        },
      ),

      // 2. runCode: Validate inventory for sale products
      n(
        "2",
        "runCode",
        "Validate inventory levels for all products included in flash sale",
        240,
        {
          codeDetails: {
            code: `const payload = JSON.parse(input.webhook_payload || '{}');
output.sale_name = payload.fields?.sale_name || payload.sale_name;
output.discount_pct = payload.fields?.discount_percentage || payload.discount_percentage;
output.product_ids = payload.fields?.product_ids || payload.product_ids;
output.start_time = payload.fields?.start_time || payload.start_time;
output.end_time = payload.fields?.end_time || payload.end_time;
output.vip_early_access = payload.fields?.vip_early_access || false;
output.subscriber_count = payload.fields?.subscriber_count || 0;
output.inventory_sufficient = true;
output.sale_code = 'FLASH' + output.discount_pct;`,
          },
        },
      ),

      // 3. Shopify: Update product prices with sale discount
      n(
        "3",
        "hitApi",
        "Shopify: Apply flash sale pricing to all included products",
        480,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/price_rules.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "price_rule",
                value:
                  '{"title":"{sale_name}","value":"-{discount_pct}","value_type":"percentage","starts_at":"{start_time}","ends_at":"{end_time}","entitled_product_ids":{product_ids}}',
              },
            ],
            variables: [
              {
                name: "price_rule.id",
                customName: "price_rule_id",
                type: "string",
              },
              {
                name: "price_rule.title",
                customName: "rule_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Email: Notify subscribers
      emailNode(
        "4",
        "Email: Send flash sale announcement to subscriber list",
        720,
        "{subscriber_list_email}",
        "{sale_name}: {discount_pct}% Off - Limited Time Only!",
        "<h1>{sale_name}</h1><h2>{discount_pct}% OFF Everything</h2><p>The sale starts NOW and ends {end_time}. Don't miss out on these incredible deals.</p><p>Use code <strong>{sale_code}</strong> at checkout.</p><p><a href='https://shop.example.com/sale'>Shop the Sale</a></p><p>Hurry - once stock runs out, it's gone!</p>",
      ),

      // 5. SMS: VIP early access
      smsNode(
        "5",
        "SMS: Send VIP early access notification to top customers",
        960,
        "{vip_phone_list}",
        "VIP EARLY ACCESS: {sale_name} - {discount_pct}% off starts now! Code: {sale_code}. Shop first: shop.example.com/sale",
      ),

      // 6. Slack: Announce launch to team
      slackNode(
        "6",
        "Slack: Announce flash sale launch to operations and support teams",
        1200,
        "Flash Sale LIVE: {sale_name} ({discount_pct}% off) is now active. Price rule #{price_rule_id} applied. Sale runs until {end_time}. Subscriber emails sent to {subscriber_count} contacts. Monitor inventory closely.",
      ),

      // 7. Condition: Stock levels need monitoring?
      n(
        "7",
        "condition",
        "Is inventory sufficient for expected flash sale demand?",
        1440,
        {
          conditionData: [
            {
              variable: "inventory_sufficient",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Google Sheets: Track sale launch metrics
      sheetsNode(
        "8",
        "Google Sheets: Log flash sale launch details and initial metrics",
        1680,
        "FlashSales",
        '[["{ sale_name }","{ discount_pct }","{ start_time }","{ end_time }","{ price_rule_id }","{ subscriber_count }","{ inventory_sufficient }","launched"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Inventory OK -> log as healthy
      e("7", "8", "output_2"), // Inventory concern -> log with warning
    ],
    summary:
      "Orchestrates flash sale launches from approval through execution. When a sale is approved in Airtable, the workflow validates inventory, applies discount pricing in Shopify, sends announcement emails to subscribers, texts VIP customers with early access, notifies the operations team via Slack, checks inventory readiness, and logs all launch metrics to Google Sheets for performance tracking.",
  },

  // ===================================================================
  // 278. PREORDER WAITLIST TO LAUNCH ALERT — Pattern D (Catalog Alert)
  // ===================================================================

  "Preorder Waitlist to Launch Alert": {
    nodes: [
      trigger(
        "Webhook: Product now available on Shopify after preorder period",
        "webhook_app",
        {
          source: "Shopify",
          event: "products/update",
          howItStarts:
            "Shopify fires a webhook when a previously out-of-stock or preorder product becomes available, triggering notifications to the waitlist subscribers",
          setup:
            "Configure Shopify webhook for products/update. The workflow checks if the product status changed from 'draft' or unavailable to 'active' with inventory. Include product details and variant info.",
          inputFields: [],
        },
      ),

      // 2. runCode: Fetch waitlist subscribers
      n(
        "2",
        "runCode",
        "Fetch waitlist subscribers for the newly available product",
        240,
        {
          codeDetails: {
            code: `const payload = JSON.parse(input.webhook_payload || '{}');
output.product_id = payload.id || payload.product_id;
output.product_title = payload.title || payload.product_title;
output.product_url = payload.handle ? 'https://shop.example.com/products/' + payload.handle : '';
output.product_price = payload.variants?.[0]?.price || payload.price;
output.available_quantity = payload.variants?.[0]?.inventory_quantity || 0;
output.product_image = payload.image?.src || payload.images?.[0]?.src || '';
output.waitlist_count = 0;
output.was_preorder = payload.tags?.includes('preorder') || false;`,
          },
        },
      ),

      // 3. Email: Launch notification to waitlist
      emailNode(
        "3",
        "Email: Send product launch notification to waitlist subscribers",
        480,
        "{waitlist_emails}",
        "It's Here! {product_title} is Now Available",
        "<h2>{product_title} Has Arrived!</h2><p>Great news! The product you've been waiting for is finally available.</p><p><strong>{product_title}</strong> - ${product_price}</p><p>Only {available_quantity} units available - first come, first served for waitlist members.</p><p><a href='{product_url}?utm_source=waitlist&utm_campaign=launch'>Shop Now - Priority Access</a></p><p>Thank you for your patience!</p>",
      ),

      // 4. SMS: Priority access
      smsNode(
        "4",
        "SMS: Send priority purchase access link to waitlist VIP subscribers",
        720,
        "{waitlist_phone}",
        "WAITLIST ALERT: {product_title} is NOW available! Priority access: {product_url}?src=sms. Only {available_quantity} left!",
      ),

      // 5. Shopify: Update product availability status
      n(
        "5",
        "hitApi",
        "Shopify: Update product tags and metafields for launch status",
        960,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products/{product_id}.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "product",
                value:
                  '{"id":"{product_id}","tags":"launched,in-stock,was-preorder","metafields":[{"namespace":"waitlist","key":"launch_date","value":"{launch_date}","type":"date"}]}',
              },
            ],
            variables: [
              {
                name: "product.id",
                customName: "updated_product_id",
                type: "string",
              },
              {
                name: "product.tags",
                customName: "updated_tags",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Slack: Notify team about product launch
      slackNode(
        "6",
        "Slack: Notify merchandising team about product launch from preorder",
        1200,
        "Product Launch: {product_title} is now live! {available_quantity} units available. Waitlist of {waitlist_count} notified via email and SMS. Product ID: {product_id}. Monitor sales velocity.",
      ),

      // 7. Delay 24h: Track initial sales
      n(
        "7",
        "delayAction",
        "Wait 24 hours to measure initial launch performance",
        1440,
        {
          delayDetails: { duration: 24, unit: "hours" },
        },
      ),

      // 8. Google Sheets: Log conversion tracking
      sheetsNode(
        "8",
        "Google Sheets: Log launch metrics and waitlist conversion rates",
        1680,
        "LaunchTracking",
        '[["{ product_id }","{ product_title }","{ available_quantity }","{ waitlist_count }","{ product_price }","launched"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Notifies preorder waitlist subscribers when a product becomes available on Shopify. When a product status changes to available, the workflow fetches waitlist data, sends email launch notifications, texts VIP subscribers with priority access, updates Shopify tags, notifies the merchandising team via Slack, and after 24 hours logs conversion metrics to Google Sheets.",
  },

  // ===================================================================
  // 279. GIFT CARD ISSUE OR REISSUE WORKFLOW — Pattern F (Renewal/Dunning)
  // ===================================================================

  "Gift Card Issue or Reissue Workflow": {
    nodes: [
      trigger(
        "Webhook: Gift card request submitted via form or support ticket",
        "webhook_form",
        {
          source: "Form",
          event: "gift_card.requested",
          howItStarts:
            "A webhook fires when a customer submits a gift card request form or a support agent initiates a gift card issue/reissue through the help desk",
          setup:
            "Create a gift card request form that POSTs to this workflow URL. Include request type (new/reissue), amount, recipient details, and reason for reissue if applicable.",
          inputFields: [],
        },
      ),

      // 2. runCode: Validate gift card request
      n(
        "2",
        "runCode",
        "Validate gift card request details and determine if new issue or reissue",
        240,
        {
          codeDetails: {
            code: `const payload = JSON.parse(input.webhook_payload || '{}');
output.request_type = payload.request_type || 'new';
output.is_reissue = output.request_type === 'reissue';
output.card_amount = payload.amount || 25;
output.recipient_name = payload.recipient_name || payload.customer_name;
output.recipient_email = payload.recipient_email || payload.customer_email;
output.sender_name = payload.sender_name || 'Gift Card Team';
output.message = payload.personal_message || '';
output.old_card_code = payload.old_card_code || '';
output.reissue_reason = payload.reason || '';
output.request_valid = output.card_amount > 0 && output.card_amount <= 500;`,
          },
        },
      ),

      // 3. Shopify: Create gift card
      n(
        "3",
        "hitApi",
        "Shopify: Create new gift card with specified amount",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/gift_cards.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "gift_card",
                value:
                  '{"initial_value":"{card_amount}","note":"Request type: {request_type}. Recipient: {recipient_name}"}',
              },
            ],
            variables: [
              {
                name: "gift_card.id",
                customName: "gift_card_id",
                type: "string",
              },
              {
                name: "gift_card.code",
                customName: "gift_card_code",
                type: "string",
              },
              {
                name: "gift_card.balance",
                customName: "card_balance",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. Condition: Is this a reissue (void old card)?
      n(
        "4",
        "condition",
        "Is this a gift card reissue requiring the old card to be voided?",
        720,
        {
          conditionData: [
            {
              variable: "is_reissue",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Shopify: Void old gift card
      n(
        "5",
        "hitApi",
        "Shopify: Disable the old gift card to prevent duplicate use",
        960,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/gift_cards/{old_card_id}.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "gift_card",
                value:
                  '{"disabled_at":"{current_timestamp}","note":"Voided: reissued as #{gift_card_id}. Reason: {reissue_reason}"}',
              },
            ],
            variables: [
              {
                name: "gift_card.id",
                customName: "voided_card_id",
                type: "string",
              },
              {
                name: "gift_card.disabled_at",
                customName: "void_date",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Email: Deliver gift card
      emailNode(
        "6",
        "Email: Deliver new gift card to recipient with redemption instructions",
        1200,
        "{recipient_email}",
        "You received a ${card_amount} gift card from {sender_name}!",
        "<h2>You've Got a Gift!</h2><p>Hi {recipient_name},</p><p>{sender_name} sent you a <strong>${card_amount}</strong> gift card.</p>{message ? '<p><em>\"' + message + '\"</em></p>' : ''}<p><strong>Your gift card code:</strong> {gift_card_code}</p><p><a href='https://shop.example.com?gift_card={gift_card_code}'>Redeem Now</a></p><p>This card never expires and can be used across our entire store.</p>",
      ),

      // 7. Slack: Log gift card activity
      slackNode(
        "7",
        "Slack: Log gift card issuance in operations channel",
        1440,
        "Gift card {request_type}: #{gift_card_id} for ${card_amount} issued to {recipient_email}. {is_reissue ? 'Old card ' + old_card_code + ' voided. Reason: ' + reissue_reason : 'New gift card.'}",
      ),

      // 8. Google Sheets: Audit log
      sheetsNode(
        "8",
        "Google Sheets: Log gift card transaction for audit and reconciliation",
        1680,
        "GiftCardAudit",
        '[["{ gift_card_id }","{ gift_card_code }","{ card_amount }","{ request_type }","{ recipient_email }","{ sender_name }","{ old_card_code }","active"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Reissue -> void old card first
      e("4", "6", "output_2"), // New issue -> deliver directly
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles gift card issuance and reissuance through Shopify. When a request comes in via form or support, the workflow validates the request, creates a new Shopify gift card, and for reissues voids the old card to prevent duplicate use. The new card is delivered to the recipient by email, the transaction is logged in Slack for operations visibility, and an audit trail is maintained in Google Sheets.",
  },

  // ===================================================================
  // 280. WARRANTY CLAIM INTAKE — Pattern K (Lead Scoring/Routing)
  // ===================================================================

  "Warranty Claim Intake": {
    nodes: [
      trigger(
        "Webhook: Warranty claim form submitted by customer",
        "webhook_form",
        {
          source: "Form",
          event: "warranty.claim_submitted",
          howItStarts:
            "A customer submits a warranty claim form with product details, proof of purchase, and issue description, triggering the intake and validation workflow",
          setup:
            "Create a warranty claim form that POSTs to this workflow URL. Required fields: customer email, order number, product name, purchase date, issue description, and photo upload URL.",
          inputFields: [],
        },
      ),

      // 2. AI: Extract product and issue details
      aiNode(
        "2",
        "AI: Extract warranty claim details and classify issue severity",
        240,
        "You are a warranty claims processor. Extract customer_name, customer_email, order_number, product_name, product_sku, purchase_date, issue_description, issue_category (defect, damage, missing_parts, malfunction), severity (low, medium, high, critical), and has_proof_of_purchase (boolean) from the warranty claim. Return JSON.",
        "Extract and classify this warranty claim: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_sku", as: "product_sku", type: "string" },
          { field: "issue_category", as: "issue_category", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "purchase_date", as: "purchase_date", type: "string" },
        ],
      ),

      // 3. Shopify: Verify purchase
      n(
        "3",
        "hitApi",
        "Shopify: Verify original purchase and order details",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json?name={order_number}&status=any",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "orders[0].id",
                customName: "shopify_order_id",
                type: "string",
              },
              {
                name: "orders[0].created_at",
                customName: "order_date",
                type: "string",
              },
              {
                name: "orders[0].total_price",
                customName: "order_total",
                type: "string",
              },
              {
                name: "orders[0].customer.email",
                customName: "verified_email",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. runCode: Check warranty period
      n(
        "4",
        "runCode",
        "Check if product is within warranty period and validate claim",
        720,
        {
          codeDetails: {
            code: `const purchaseDate = new Date(input.purchase_date || input.order_date);
const now = new Date();
const daysSincePurchase = Math.floor((now - purchaseDate) / (1000 * 60 * 60 * 24));
const warrantyDays = 365;
output.days_since_purchase = daysSincePurchase;
output.warranty_valid = daysSincePurchase <= warrantyDays;
output.warranty_remaining = Math.max(0, warrantyDays - daysSincePurchase);
output.purchase_verified = !!input.shopify_order_id;
output.claim_eligible = output.warranty_valid && output.purchase_verified;
output.claim_priority = input.severity === 'critical' ? 'P1' : input.severity === 'high' ? 'P2' : 'P3';`,
          },
        },
      ),

      // 5. Condition: Is the claim valid and within warranty?
      n(
        "5",
        "condition",
        "Is the warranty claim valid (purchase verified and within warranty period)?",
        960,
        {
          conditionData: [
            {
              variable: "claim_eligible",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Jira: Create warranty claim ticket
      n(
        "6",
        "hitApi",
        "Jira: Create warranty claim ticket for processing team",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"WC"},"issuetype":{"name":"Bug"},"summary":"Warranty Claim: {product_name} - {issue_category}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Customer: {customer_name} ({customer_email}). Order: #{order_number}. Product: {product_name} ({product_sku}). Issue: {issue_category}. Days since purchase: {days_since_purchase}. Warranty remaining: {warranty_remaining} days. Priority: {claim_priority}."}]}]},"priority":{"name":"{claim_priority}"},"labels":["warranty","{issue_category}"]}',
              },
            ],
            variables: [
              { name: "id", customName: "jira_ticket_id", type: "string" },
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
      ),

      // 7. Email: Claim confirmation
      emailNode(
        "7",
        "Email: Send warranty claim confirmation with ticket reference",
        1440,
        "{customer_email}",
        "Warranty Claim Received - Reference #{jira_ticket_key}",
        "<h2>Warranty Claim Confirmed</h2><p>Hi {customer_name},</p><p>We've received your warranty claim for <strong>{product_name}</strong>.</p><p><strong>Reference:</strong> #{jira_ticket_key}<br/><strong>Category:</strong> {issue_category}<br/><strong>Priority:</strong> {claim_priority}<br/><strong>Warranty Status:</strong> Valid ({warranty_remaining} days remaining)</p><p>Our team will review your claim and respond within 2-3 business days. You'll receive updates at this email address.</p>",
      ),

      // 8. Google Sheets: Log claim
      sheetsNode(
        "8",
        "Google Sheets: Log warranty claim for tracking and analytics",
        1680,
        "WarrantyClaims",
        '[["{ jira_ticket_key }","{ customer_name }","{ customer_email }","{ product_name }","{ product_sku }","{ issue_category }","{ severity }","{ claim_eligible }","{ days_since_purchase }","open"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Valid claim -> create Jira ticket
      e("5", "7", "output_2"), // Invalid claim -> send denial email
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes warranty claims from intake through validation and ticket creation. When a customer submits a warranty claim form, AI extracts and classifies the issue, Shopify verifies the original purchase, warranty period eligibility is checked, and valid claims get a Jira ticket created for the processing team. The customer receives a confirmation email with their reference number, and all claims are logged to Google Sheets for analytics.",
  },
};
